import React from "react"
import {useTranslation} from "react-i18next"

import {GenericErrorAlert} from "../../components/Alert"
import {LayoutBlock} from "../../components/Layout/LayoutBlock"
import {Loading} from "../../components/Loading"
import {useReachCampaignsQuery} from "../../queries/reachEngine"
import {useDocumentTitle} from "../../utils/hooks"
import {ChannelsByResponsesCard} from "./components/ChannelsByResponsesCard"
import {FlowTypeCard} from "./components/FlowTypeCard"
import {LeadPoolCard} from "./components/LeadPoolCard"
import {OutreachDepthCard} from "./components/OutreachDepthCard"
import {PeriodSelector} from "./components/PeriodSelector"
import {ResultsOverTimeCard} from "./components/ResultsOverTimeCard"
import {SalesFlowChannelsCard} from "./components/SalesFlowChannelsCard"
import {SalesFlowsCard} from "./components/SalesFlowsCard"
import {TotalResultsCard} from "./components/TotalResultsCard"
import {getPeriodRange} from "./period"
import {useReachEnginePeriod} from "./useReachEnginePeriod"

export const ReachEngine: React.FC = () => {
  const {t} = useTranslation()

  useDocumentTitle(t("ReachEngine_DocumentTitle"))

  const [period, setPeriod] = useReachEnginePeriod()
  const range = React.useMemo(() => getPeriodRange(period), [period])
  const {data, error, isLoading, isFetching, refetch} = useReachCampaignsQuery(range)

  return (
    <LayoutBlock outerClassName={"py-10 grow"} innerClassName={"flex flex-col gap-6"}>
      <div className={"flex flex-wrap items-center justify-between gap-3"}>
        <div className={"flex items-center gap-3"}>
          <div>
            <h1 className={"text-2xl font-bold tracking-tight text-cr-grey-90"}>{t("ReachEngine_PageTitle")}</h1>
            <p className={"mt-1 text-sm text-cr-grey-50"}>{t("ReachEngine_PageSubtitle")}</p>
          </div>
          {isFetching && !isLoading && <Loading size={"sm"} fullSpace={false} delayShow={false} />}
        </div>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {isLoading ? (
        <Loading size={"xl"} />
      ) : error || !data ? (
        <GenericErrorAlert retry={refetch} />
      ) : (
        <div className={"flex flex-col gap-6"}>
          <div className={"grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"}>
            <LeadPoolCard leadPool={data.lead_pool} />
            <SalesFlowsCard salesFlows={data.sales_flows} />
            <FlowTypeCard flowTypes={data.flow_types} className={"md:col-span-2 xl:col-span-1"} />
          </div>
          <TotalResultsCard results={data.results} />
          <ResultsOverTimeCard range={range} period={period} funnel={data.funnel} prospects={data.flow_types.leads} />
          <SalesFlowChannelsCard range={range} />
          <ChannelsByResponsesCard range={range} />
          <OutreachDepthCard range={range} />
        </div>
      )}
    </LayoutBlock>
  )
}
